import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';
import type RouterService from '@ember/routing/router-service';
import {
  findContentSource,
  getMentionsForContentSource,
  getRecipesForContentSource,
} from '../../builders/content-sources.ts';
import type { ContentSource } from '../../schemas/content-source.ts';
import type { Mention } from '../../schemas/mention.ts';
import type { Recipe } from '../../schemas/recipe.ts';

interface SourceParams {
  source_id: string;
}

interface CachedData {
  source: ContentSource;
  mentions: Mention[];
  recipes: Recipe[];
}

export default class SourcesShowRoute extends Route {
  @service declare store: Store;
  @service declare router: RouterService;

  queryParams = {
    mentionId: { refreshModel: true },
  };

  #cachedData = new Map<string, CachedData>();
  #forceRefresh = false;

  clearCache(sourceId: string): void {
    this.#cachedData.delete(sourceId);
  }

  async model(params: Record<string, unknown>) {
    const { source_id, mentionId } = params as unknown as SourceParams & {
      mentionId?: string;
    };

    const isForced = this.#forceRefresh;
    this.#forceRefresh = false;

    if (isForced) {
      this.#cachedData.delete(source_id);
    }

    const cached = this.#cachedData.get(source_id);
    let source: ContentSource;
    let mentions: Mention[];
    let recipes: Recipe[];

    if (cached) {
      source = cached.source;
      mentions = cached.mentions;
      recipes = cached.recipes;
    } else {
      const requestOptions = isForced
        ? { ...findContentSource(source_id), cacheOptions: { reload: true } }
        : findContentSource(source_id);
      const mentionOptions = isForced
        ? {
            ...getMentionsForContentSource(source_id),
            cacheOptions: { reload: true },
          }
        : getMentionsForContentSource(source_id);
      const recipeOptions = isForced
        ? {
            ...getRecipesForContentSource(source_id),
            cacheOptions: { reload: true },
          }
        : getRecipesForContentSource(source_id);

      const [sourceResult, mentionsResult, recipesResult] = await Promise.all([
        this.store.request<{ data: ContentSource }>(requestOptions),
        this.store.request<{ data: Mention[] }>(mentionOptions),
        this.store.request<{ data: Recipe[] }>(recipeOptions),
      ]);

      source = sourceResult.content.data;
      mentions = mentionsResult.content.data;
      recipes = recipesResult.content.data;

      this.#cachedData.set(source_id, { source, mentions, recipes });
    }

    return {
      source,
      mentions,
      recipes,
      mentionId,
      clearCache: () => this.clearCache(source_id),
      onDeleted: () => {
        this.clearCache(source_id);
        this.router.transitionTo('sources');
      },
      refresh: () => {
        this.#forceRefresh = true;
        return this.refresh();
      },
    };
  }
}
